import { defineStore } from "pinia";
import accountService from "@/services/account.service";
import roomsService from "@/services/rooms.service";
import bookingsService from "@/services/bookings.service";
import type { HotelReviewItem } from "@/services/account.service";
import type {
  Hotel,
  HotelDetails,
  HotelRoom,
  RoomDetails,
  RoomBookingSchedule,
} from "@/types/api.types";

export interface HotelDisplay {
  id: string;
  name: string;
  location: string;
  image: string;
  rating: number;
  price: number;
  description: string;
  type: string;
  address: string;
  city: string;
  state: string;
  imageUrls: string[];
  distance?: string;
  distanceInKm?: number;
}

export interface Pagination {
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  totalRecords: number;
}

export const useHotelStore = defineStore("hotel", {
  state: () => ({
    featuredHotels: [] as HotelDisplay[],
    nearbyHotels: [] as HotelDisplay[],
    currentHotelDetails: null as HotelDetails | null,
    currentRoomDetails: null as RoomDetails | null,
    roomBookingSchedule: [] as RoomBookingSchedule[],
    hotelRooms: [] as HotelRoom[],
    isLoading: false,
    isLoadingNearby: false,
    isLoadingDetails: false,
    isLoadingRooms: false,
    isLoadingRoomDetails: false,
    isLoadingBookingSchedule: false,
    isSubmittingReview: false,
    isLoadingReviews: false,
    hotelReviews: [] as HotelReviewItem[],
    hotelReviewsPagination: null as Pagination | null,
    error: "",
    nearbyError: "",
    detailsError: "",
    roomsError: "",
    roomDetailsError: "",
    bookingScheduleError: "",
    submitReviewError: "",
    reviewsError: "",
    userLocation: null as { latitude: number; longitude: number } | null,
    nearbyPagination: null as Pagination | null,
    lastSearchParams: null as {
      latitude?: number;
      longitude?: number;
      searchTerm?: string;
      stateId?: string;
      cityId?: string;
      propertyType?: string;
    } | null,
  }),

  getters: {
    hasFeaturedHotels: (state) => state.featuredHotels.length > 0,
    hasNearbyHotels: (state) => state.nearbyHotels.length > 0,
    hasMoreNearbyPages: (state) =>
      state.nearbyPagination
        ? state.nearbyPagination.pageNumber < state.nearbyPagination.totalPages
        : false,
    // Derived from the reviews actually on the current page, not the
    // hotel's possibly-stale `rating` field from get-hotel.
    hotelReviewsAverage: (state) => {
      if (state.hotelReviews.length === 0) return 0;
      const sum = state.hotelReviews.reduce((acc, r) => acc + r.rating, 0);
      return sum / state.hotelReviews.length;
    },
    hotelReviewsCount: (state) => state.hotelReviewsPagination?.totalRecords ?? 0,
  },

  actions: {
    async fetchFeaturedHotels() {
      this.isLoading = true;
      this.error = "";

      try {
        const response = await accountService.getFeaturedHotels();

        console.log("API Response:", response);

        // The HTTP client returns response.data directly
        if (response && response.data) {
          // Map API response to display format
          this.featuredHotels = response.data.map((hotel: Hotel) => ({
            id: hotel.id,
            name: hotel.name,
            location: `${hotel.city}, ${hotel.state}`,
            image: hotel.imageUrls[0] || "",
            rating: hotel.rating || 0,
            price: hotel.amount || 0,
            description: hotel.description || "",
            type: hotel.propertyType || "Hotel",
            address: hotel.address,
            city: hotel.city,
            state: hotel.state,
            imageUrls: hotel.imageUrls,
          }));

          console.log("Featured hotels loaded:", this.featuredHotels.length);
          console.log("Featured hotels data:", this.featuredHotels);
        } else {
          console.warn("No data in response:", response);
        }
      } catch (error: any) {
        console.error("Error fetching featured hotels:", error);
        this.error =
          error?.response?.data?.message ||
          error?.message ||
          "Failed to load featured hotels. Please try again later.";
      } finally {
        this.isLoading = false;
      }
    },

    async fetchNearbyHotels(
      latitude?: number,
      longitude?: number,
      searchTerm?: string,
      stateId?: string,
      cityId?: string,
      propertyType?: string,
      pageNumber = 1,
      pageSize = 12
    ) {
      this.isLoadingNearby = true;
      this.nearbyError = "";

      try {
        const response = await accountService.getNearbyHotels({
          latitude,
          longitude,
          searchTerm,
          stateId,
          cityId,
          propertyType,
          pageNumber,
          pageSize,
        });

        console.log("Nearby Hotels API Response:", response);

        if (response && response.data) {
          const { items, pagination } = response.data;

          // Map API response to display format
          const mappedHotels = items.map(
            (hotel: Hotel & { distanceInKm: number }) => ({
              id: hotel.id,
              name: hotel.name,
              location: `${hotel.city}, ${hotel.state}`,
              image: hotel.imageUrls[0] || "",
              rating: hotel.rating || 0,
              price: hotel.amount || 0,
              description: hotel.description || "No description available",
              type: hotel.propertyType || "Hotel",
              address: hotel.address,
              city: hotel.city,
              state: hotel.state,
              imageUrls: hotel.imageUrls,
              distanceInKm: hotel.distanceInKm,
              distance:
                hotel.distanceInKm > 0
                  ? `${hotel.distanceInKm.toFixed(1)} km away`
                  : "Location not available",
            })
          );

          // Append to existing hotels for pagination
          if (pageNumber === 1) {
            this.nearbyHotels = mappedHotels;
          } else {
            this.nearbyHotels.push(...mappedHotels);
          }

          this.nearbyPagination = pagination;
          if (latitude !== undefined && longitude !== undefined) {
            this.userLocation = { latitude, longitude };
          }
          this.lastSearchParams = {
            latitude,
            longitude,
            searchTerm,
            stateId,
            cityId,
            propertyType,
          };

          console.log("Nearby hotels loaded:", this.nearbyHotels.length);
          console.log("Pagination:", this.nearbyPagination);
        } else {
          console.warn("No data in nearby hotels response:", response);
        }
      } catch (error: any) {
        console.error("Error fetching nearby hotels:", error);
        this.nearbyError =
          error?.response?.data?.message ||
          error?.message ||
          "Failed to load nearby hotels. Please try again later.";
      } finally {
        this.isLoadingNearby = false;
      }
    },

    async loadMoreNearbyHotels() {
      if (!this.nearbyPagination || !this.lastSearchParams) return;

      const nextPage = this.nearbyPagination.pageNumber + 1;
      if (nextPage <= this.nearbyPagination.totalPages) {
        await this.fetchNearbyHotels(
          this.lastSearchParams.latitude,
          this.lastSearchParams.longitude,
          this.lastSearchParams.searchTerm,
          this.lastSearchParams.stateId,
          this.lastSearchParams.cityId,
          this.lastSearchParams.propertyType,
          nextPage,
          this.nearbyPagination.pageSize
        );
      }
    },

    clearError() {
      this.error = "";
      this.nearbyError = "";
      this.detailsError = "";
      this.roomsError = "";
      this.roomDetailsError = "";
      this.bookingScheduleError = "";
      this.submitReviewError = "";
    },

    async fetchHotelDetails(hotelId: string) {
      this.isLoadingDetails = true;
      this.detailsError = "";
      this.currentHotelDetails = null;

      try {
        const response = await accountService.getHotelDetails(hotelId);

        console.log("Hotel Details API Response:", response);

        if (response && response.data) {
          this.currentHotelDetails = response.data;
          this.currentHotelDetails.reviews =
            this.currentHotelDetails.reviews || [];
          console.log("Hotel details loaded:", this.currentHotelDetails);
        } else {
          console.warn("No data in hotel details response:", response);
        }
      } catch (error: any) {
        console.error("Error fetching hotel details:", error);
        this.detailsError =
          error?.response?.data?.message ||
          error?.message ||
          "Failed to load hotel details. Please try again later.";
      } finally {
        this.isLoadingDetails = false;
      }
    },

    clearHotelDetails() {
      this.currentHotelDetails = null;
      this.detailsError = "";
      this.hotelRooms = [];
      this.roomsError = "";
      this.clearHotelReviews();
    },

    async fetchHotelReviews(hotelId: string, pageNumber = 1, pageSize = 10) {
      this.isLoadingReviews = true;
      this.reviewsError = "";

      try {
        const response = await accountService.getHotelReviews(
          hotelId,
          pageNumber,
          pageSize
        );

        if (response && response.data) {
          this.hotelReviews = response.data.items || [];
          this.hotelReviewsPagination = response.data.pagination || null;
        } else {
          console.warn("No data in hotel reviews response:", response);
        }
      } catch (error: any) {
        console.error("Error fetching hotel reviews:", error);
        this.reviewsError =
          error?.message || "Unable to load reviews. Please try again later.";
      } finally {
        this.isLoadingReviews = false;
      }
    },

    clearHotelReviews() {
      this.hotelReviews = [];
      this.hotelReviewsPagination = null;
      this.reviewsError = "";
    },

    async fetchHotelRooms(hotelId: string) {
      this.isLoadingRooms = true;
      this.roomsError = "";
      this.hotelRooms = [];

      try {
        const response = await roomsService.getHotelRooms(hotelId);

        console.log("Hotel Rooms API Response:", response);

        if (response && response.data && response.data.rooms) {
          this.hotelRooms = response.data.rooms;
          console.log("Hotel rooms loaded:", this.hotelRooms.length);
        } else {
          console.warn("No rooms in response:", response);
        }
      } catch (error: any) {
        console.error("Error fetching hotel rooms:", error);
        this.roomsError =
          error?.response?.data?.message ||
          error?.message ||
          "Failed to load rooms. Please try again later.";
      } finally {
        this.isLoadingRooms = false;
      }
    },

    clearHotelRooms() {
      this.hotelRooms = [];
      this.roomsError = "";
    },

    async fetchRoomDetails(roomId: string) {
      this.isLoadingRoomDetails = true;
      this.roomDetailsError = "";
      this.currentRoomDetails = null;

      try {
        const response = await roomsService.getRoomDetails(roomId);

        console.log("Room Details API Response:", response);

        if (response && response.data) {
          this.currentRoomDetails = response.data;
          console.log("Room details loaded:", this.currentRoomDetails);
        } else {
          console.warn("No data in room details response:", response);
        }
      } catch (error: any) {
        console.error("Error fetching room details:", error);
        this.roomDetailsError =
          error?.response?.data?.message ||
          error?.message ||
          "Failed to load room details. Please try again later.";
      } finally {
        this.isLoadingRoomDetails = false;
      }
    },

    clearRoomDetails() {
      this.currentRoomDetails = null;
      this.roomDetailsError = "";
      this.roomBookingSchedule = [];
      this.bookingScheduleError = "";
    },

    async fetchRoomBookingSchedule(roomId: string) {
      this.isLoadingBookingSchedule = true;
      this.bookingScheduleError = "";
      this.roomBookingSchedule = [];

      try {
        const response = await bookingsService.getRoomBookingSchedule(roomId);

        console.log("Room Booking Schedule API Response:", response);

        if (response && response.data) {
          this.roomBookingSchedule = response.data;
          console.log(
            "Room booking schedule loaded:",
            this.roomBookingSchedule
          );
        } else {
          console.warn("No data in booking schedule response:", response);
        }
      } catch (error: any) {
        console.error("Error fetching room booking schedule:", error);
        this.bookingScheduleError =
          error?.response?.data?.message ||
          error?.message ||
          "Failed to load booking schedule. Please try again later.";
      } finally {
        this.isLoadingBookingSchedule = false;
      }
    },

    clearBookingSchedule() {
      this.roomBookingSchedule = [];
      this.bookingScheduleError = "";
    },

    async submitReview(hotelId: string, rating: number, comment: string) {
      this.isSubmittingReview = true;
      this.submitReviewError = "";

      try {
        const response = await accountService.addReview({
          hotelId,
          rating,
          comment,
        });

        // Reload from page 1 so the new review - and the rating/count
        // derived from it - show up immediately.
        try {
          await this.fetchHotelReviews(
            hotelId,
            1,
            this.hotelReviewsPagination?.pageSize || 10
          );
        } catch (refreshError) {
          console.error("Error refreshing reviews:", refreshError);
        }

        return response;
      } catch (error: any) {
        this.submitReviewError =
          error?.message || "Unable to submit your review. Please try again.";
        throw error;
      } finally {
        this.isSubmittingReview = false;
      }
    },
  },
});
